// src/pages/Services.jsx

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import {
  HiArrowRight,
  HiArrowUpRight,
  HiBolt,
  HiBuildingOffice2,
  HiCheckCircle,
  HiSparkles,
  HiUserGroup,
} from "react-icons/hi2";
import { Link } from "react-router-dom";

import services from "../data/services";
import ServiceCard from "../components/ServiceCard";

const stats = [
  {
    value: 1000,
    suffix: "+",
    label: "Projects Completed",
    note: "Academic, business & digital work",
    icon: HiBolt,
  },
  {
    value: 5000,
    suffix: "+",
    label: "Students Trained",
    note: "Practical and career-focused learning",
    icon: HiUserGroup,
  },
  {
    value: 50,
    suffix: "+",
    label: "Companies Served",
    note: "Across technology and business needs",
    icon: HiBuildingOffice2,
  },
];

const Services = () => {
  const [activeService, setActiveService] = useState(0);

  const selectedService =
    services[activeService] || services[0];

  const serviceCount = services.length;

  const portfolioLabel = useMemo(() => {
    if (serviceCount === 1) {
      return "1 capability";
    }

    return `${serviceCount} capabilities`;
  }, [serviceCount]);

  if (!services.length) {
    return (
      <main
        className="
          min-h-screen
          bg-white
          px-4
          py-20
          text-center
          dark:bg-gray-950
        "
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Services are currently unavailable.
        </p>
      </main>
    );
  }

  return (
    <main
      className="
        relative
        min-h-screen
        overflow-hidden
        bg-[#f8faff]
        text-gray-950
        dark:bg-[#070b14]
        dark:text-white
      "
    >
      {/* =====================================================
          GLOBAL ATMOSPHERE
      ====================================================== */}

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          left-1/2
          top-[-220px]
          h-[600px]
          w-[900px]
          -translate-x-1/2
          rounded-full
          bg-indigo-500/[0.055]
          blur-[130px]
          dark:bg-indigo-500/[0.08]
        "
      />

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          -left-32
          top-[620px]
          h-[320px]
          w-[320px]
          rounded-full
          bg-cyan-400/[0.035]
          blur-[110px]
          dark:bg-cyan-400/[0.045]
        "
      />

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          -right-32
          top-[1000px]
          h-[340px]
          w-[340px]
          rounded-full
          bg-purple-500/[0.035]
          blur-[110px]
          dark:bg-purple-500/[0.045]
        "
      />

      {/* =====================================================
          HERO
      ====================================================== */}

      <section
        className="
          relative
          px-4
          pb-12
          pt-16
          sm:px-6
          sm:pb-16
          sm:pt-20
          lg:px-8
          lg:pb-20
          lg:pt-24
        "
      >
        <div className="mx-auto max-w-7xl">

          {/* Breadcrumb / eyebrow */}
          <motion.div
            initial={{
              opacity: 0,
              y: 10,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.45,
            }}
            className="
              mb-7
              flex
              items-center
              gap-2
              text-[9px]
              font-semibold
              uppercase
              tracking-[0.16em]
              text-gray-400
              dark:text-gray-500
            "
          >
            <span>IA4Solutions</span>

            <span
              aria-hidden="true"
              className="text-gray-300 dark:text-gray-700"
            >
              /
            </span>

            <span className="text-indigo-600 dark:text-indigo-400">
              Services
            </span>
          </motion.div>

          <div
            className="
              grid
              items-end
              gap-10
              lg:grid-cols-[1.1fr_0.9fr]
              lg:gap-16
            "
          >
            {/* Hero copy */}
            <motion.div
              initial={{
                opacity: 0,
                y: 18,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.55,
                ease: "easeOut",
              }}
            >
              <div
                className="
                  inline-flex
                  items-center
                  gap-2
                  rounded-full
                  border
                  border-indigo-100
                  bg-white/75
                  px-3
                  py-1.5
                  text-[8px]
                  font-bold
                  uppercase
                  tracking-[0.17em]
                  text-indigo-600
                  shadow-sm
                  backdrop-blur
                  dark:border-indigo-900/50
                  dark:bg-gray-900/70
                  dark:text-indigo-400
                "
              >
                <span
                  className="
                    h-1.5
                    w-1.5
                    rounded-full
                    bg-indigo-500
                    shadow-[0_0_0_4px_rgba(99,102,241,0.10)]
                  "
                />

                What We Do
              </div>

              <h1
                className="
                  mt-5
                  max-w-4xl
                  text-[2.65rem]
                  font-black
                  leading-[0.94]
                  tracking-[-0.065em]
                  text-gray-950
                  sm:text-5xl
                  lg:text-[4.65rem]
                  dark:text-white
                "
              >
                One ecosystem.
                <br />

                <span
                  className="
                    bg-gradient-to-r
                    from-indigo-600
                    via-blue-600
                    to-cyan-500
                    bg-clip-text
                    text-transparent
                  "
                >
                  Multiple ways forward.
                </span>
              </h1>

              <p
                className="
                  mt-6
                  max-w-2xl
                  text-sm
                  leading-7
                  text-gray-600
                  sm:text-[15px]
                  dark:text-gray-300
                "
              >
                From technology training and academic projects to
                business solutions and digital growth, IA4Solutions
                brings practical expertise together in one place.
              </p>

              <div
                className="
                  mt-7
                  flex
                  flex-wrap
                  items-center
                  gap-3
                "
              >
                <Link
                  to="/contact"
                  className="
                    group
                    inline-flex
                    items-center
                    gap-2
                    rounded-xl
                    bg-gray-950
                    px-5
                    py-3
                    text-[11px]
                    font-bold
                    text-white
                    shadow-[0_12px_30px_rgba(15,23,42,0.13)]
                    transition-all
                    duration-200
                    hover:-translate-y-0.5
                    hover:bg-indigo-600
                    dark:bg-white
                    dark:text-gray-950
                    dark:hover:bg-indigo-500
                    dark:hover:text-white
                  "
                >
                  Start a Conversation

                  <HiArrowRight
                    className="
                      transition-transform
                      duration-200
                      group-hover:translate-x-0.5
                    "
                  />
                </Link>

                <a
                  href="#service-command-center"
                  className="
                    inline-flex
                    items-center
                    gap-2
                    rounded-xl
                    border
                    border-gray-200
                    bg-white/80
                    px-5
                    py-3
                    text-[11px]
                    font-bold
                    text-gray-700
                    shadow-sm
                    backdrop-blur
                    transition-all
                    duration-200
                    hover:-translate-y-0.5
                    hover:border-indigo-200
                    hover:text-indigo-600
                    dark:border-gray-800
                    dark:bg-gray-900/70
                    dark:text-gray-200
                    dark:hover:border-indigo-800
                    dark:hover:text-indigo-400
                  "
                >
                  Explore Services
                  <HiArrowRight className="text-sm" />
                </a>
              </div>
            </motion.div>

            {/* Hero side panel */}
            <motion.div
              initial={{
                opacity: 0,
                y: 18,
                scale: 0.985,
              }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
              }}
              transition={{
                duration: 0.6,
                delay: 0.08,
                ease: "easeOut",
              }}
              className="relative"
            >
              <div
                className="
                  relative
                  overflow-hidden
                  rounded-[28px]
                  border
                  border-gray-200/80
                  bg-white/80
                  p-5
                  shadow-[0_22px_70px_rgba(15,23,42,0.08)]
                  backdrop-blur-xl
                  dark:border-gray-800
                  dark:bg-gray-900/75
                  dark:shadow-[0_22px_70px_rgba(0,0,0,0.28)]
                  sm:p-6
                "
              >
                <div
                  aria-hidden="true"
                  className="
                    absolute
                    right-[-60px]
                    top-[-60px]
                    h-44
                    w-44
                    rounded-full
                    bg-indigo-500/[0.08]
                    blur-3xl
                    dark:bg-indigo-500/[0.12]
                  "
                />

                <div className="relative z-10">
                  <div
                    className="
                      flex
                      items-center
                      justify-between
                      gap-4
                    "
                  >
                    <div>
                      <p
                        className="
                          text-[8px]
                          font-bold
                          uppercase
                          tracking-[0.16em]
                          text-indigo-600
                          dark:text-indigo-400
                        "
                      >
                        Service Intelligence
                      </p>

                      <p
                        className="
                          mt-1
                          text-xs
                          font-semibold
                          text-gray-500
                          dark:text-gray-400
                        "
                      >
                        Discover what fits your goal.
                      </p>
                    </div>

                    <div
                      className="
                        flex
                        h-9
                        w-9
                        items-center
                        justify-center
                        rounded-xl
                        border
                        border-gray-200
                        bg-gray-50
                        text-indigo-500
                        dark:border-gray-700
                        dark:bg-gray-800
                        dark:text-indigo-400
                      "
                    >
                      <HiSparkles className="text-base" />
                    </div>
                  </div>

                  <div
                    className="
                      mt-7
                      grid
                      grid-cols-2
                      gap-3
                    "
                  >
                    {[
                      "Learn",
                      "Build",
                      "Grow",
                      "Scale",
                    ].map((item, index) => (
                      <div
                        key={item}
                        className="
                          rounded-2xl
                          border
                          border-gray-200/80
                          bg-gray-50/80
                          px-4
                          py-4
                          dark:border-gray-800
                          dark:bg-gray-950/60
                        "
                      >
                        <p
                          className="
                            text-[8px]
                            font-bold
                            uppercase
                            tracking-[0.14em]
                            text-gray-400
                          "
                        >
                          0{index + 1}
                        </p>

                        <p
                          className="
                            mt-1
                            text-sm
                            font-extrabold
                            tracking-[-0.02em]
                            text-gray-900
                            dark:text-white
                          "
                        >
                          {item}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div
                    className="
                      mt-5
                      flex
                      items-center
                      justify-between
                      gap-4
                      rounded-2xl
                      border
                      border-indigo-100
                      bg-indigo-50/70
                      px-4
                      py-3
                      dark:border-indigo-900/50
                      dark:bg-indigo-950/30
                    "
                  >
                    <div className="flex items-center gap-2">
                      <HiCheckCircle className="text-base text-emerald-500" />

                      <span
                        className="
                          text-[9px]
                          font-semibold
                          text-indigo-800
                          dark:text-indigo-200
                        "
                      >
                        Practical. Professional. Outcome-focused.
                      </span>
                    </div>

                    <HiArrowUpRight
                      className="
                        shrink-0
                        text-sm
                        text-indigo-500
                        dark:text-indigo-400
                      "
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* =====================================================
          KPI STRIP
      ====================================================== */}

      <section
        aria-label="Service statistics"
        className="relative px-4 pb-12 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <div
            className="
              grid
              grid-cols-1
              overflow-hidden
              rounded-[24px]
              border
              border-gray-200/80
              bg-white
              shadow-[0_12px_40px_rgba(15,23,42,0.05)]
              sm:grid-cols-3
              sm:divide-x
              sm:divide-gray-100
              dark:border-gray-800
              dark:bg-gray-900/90
              dark:sm:divide-gray-800
            "
          >
            {stats.map((stat, index) => {
              const StatIcon = stat.icon;

              return (
                <motion.div
                  key={stat.label}
                  initial={{
                    opacity: 0,
                    y: 10,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                    amount: 0.2,
                  }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.07,
                  }}
                  className="
                    group
                    relative
                    px-5
                    py-5
                    sm:px-6
                    sm:py-6
                    lg:px-7
                  "
                >
                  <div
                    className="
                      flex
                      items-center
                      justify-between
                      gap-4
                    "
                  >
                    <div>
                      <div
                        className="
                          flex
                          items-center
                          gap-2
                        "
                      >
                        <span
                          className="
                            h-1.5
                            w-1.5
                            rounded-full
                            bg-indigo-500
                          "
                        />

                        <p
                          className="
                            text-[8px]
                            font-bold
                            uppercase
                            tracking-[0.15em]
                            text-gray-400
                            sm:text-[9px]
                          "
                        >
                          {stat.label}
                        </p>
                      </div>

                      <p
                        className="
                          mt-1.5
                          text-[2rem]
                          font-black
                          leading-none
                          tracking-[-0.055em]
                          text-gray-950
                          sm:text-[2.2rem]
                          dark:text-white
                        "
                      >
                        <CountUp
                          end={stat.value}
                          duration={1.5}
                          enableScrollSpy
                          scrollSpyOnce
                        />

                        <span
                          className="
                            ml-1
                            text-sm
                            font-extrabold
                            text-indigo-600
                            dark:text-indigo-400
                          "
                        >
                          {stat.suffix}
                        </span>
                      </p>

                      <p
                        className="
                          mt-1.5
                          text-[9px]
                          leading-5
                          text-gray-500
                          dark:text-gray-400
                        "
                      >
                        {stat.note}
                      </p>
                    </div>

                    <div
                      className="
                        flex
                        h-10
                        w-10
                        shrink-0
                        items-center
                        justify-center
                        rounded-xl
                        border
                        border-gray-200
                        bg-gray-50
                        text-indigo-500
                        transition-all
                        duration-300
                        group-hover:-translate-y-0.5
                        group-hover:border-indigo-200
                        group-hover:bg-indigo-50
                        dark:border-gray-700
                        dark:bg-gray-800
                        dark:text-indigo-400
                        dark:group-hover:border-indigo-800
                        dark:group-hover:bg-indigo-950/50
                      "
                    >
                      <StatIcon className="text-base" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* =====================================================
          SERVICE COMMAND CENTER
      ====================================================== */}

      <section
        id="service-command-center"
        className="
          relative
          scroll-mt-20
          px-4
          py-14
          sm:px-6
          sm:py-16
          lg:px-8
          lg:py-20
        "
      >
        <div className="mx-auto max-w-7xl">

          {/* Section intro */}
          <motion.div
            initial={{
              opacity: 0,
              y: 12,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.2,
            }}
            transition={{
              duration: 0.45,
            }}
            className="max-w-3xl"
          >
            <div
              className="
                inline-flex
                items-center
                gap-2
                rounded-full
                border
                border-gray-200
                bg-white/75
                px-3
                py-1.5
                text-[8px]
                font-bold
                uppercase
                tracking-[0.16em]
                text-gray-500
                shadow-sm
                dark:border-gray-800
                dark:bg-gray-900/70
                dark:text-gray-400
              "
            >
              <span
                className="
                  h-1.5
                  w-1.5
                  rounded-full
                  bg-indigo-500
                "
              />

              Service Command Center
            </div>

            <h2
              className="
                mt-4
                max-w-3xl
                text-3xl
                font-black
                leading-tight
                tracking-[-0.045em]
                text-gray-950
                sm:text-4xl
                lg:text-[3.2rem]
                dark:text-white
              "
            >
              Don't browse services.
              <br />

              <span
                className="
                  bg-gradient-to-r
                  from-indigo-600
                  via-blue-600
                  to-cyan-500
                  bg-clip-text
                  text-transparent
                "
              >
                Find the right move.
              </span>
            </h2>

            <p
              className="
                mt-4
                max-w-2xl
                text-sm
                leading-7
                text-gray-600
                dark:text-gray-300
              "
            >
              Select a capability below and instantly understand
              where it fits, what it helps you achieve, and how
              IA4Solutions can support the next step.
            </p>
          </motion.div>

          {/* Command center */}
          <div
            className="
              mt-9
              grid
              gap-5
              lg:grid-cols-[0.82fr_1.18fr]
              lg:gap-6
            "
          >
            {/* Service navigator */}
            <div
              className="
                rounded-[26px]
                border
                border-gray-200/80
                bg-white/80
                p-3
                shadow-[0_16px_50px_rgba(15,23,42,0.055)]
                backdrop-blur
                dark:border-gray-800
                dark:bg-gray-900/75
                dark:shadow-[0_18px_50px_rgba(0,0,0,0.24)]
                sm:p-4
              "
            >
              <div
                className="
                  mb-3
                  flex
                  items-center
                  justify-between
                  gap-4
                  px-2
                "
              >
                <p
                  className="
                    text-[8px]
                    font-bold
                    uppercase
                    tracking-[0.15em]
                    text-gray-400
                  "
                >
                  Capability map
                </p>

                <span
                  className="
                    text-[8px]
                    font-semibold
                    text-gray-400
                  "
                >
                  {portfolioLabel}
                </span>
              </div>

              <div
                className="
                  flex
                  max-h-[430px]
                  flex-col
                  gap-1.5
                  overflow-y-auto
                  pr-1
                "
              >
                {services.map((service, index) => {
                  const active = index === activeService;

                  return (
                    <button
                      key={
                        service.slug ||
                        `${service.title}-${index}`
                      }
                      type="button"
                      onClick={() =>
                        setActiveService(index)
                      }
                      className={`
                        group
                        flex
                        w-full
                        items-center
                        gap-3
                        rounded-2xl
                        border
                        px-3
                        py-3
                        text-left
                        transition-all
                        duration-200
                        ${
                          active
                            ? `
                              border-indigo-200
                              bg-indigo-50
                              shadow-sm
                              dark:border-indigo-800
                              dark:bg-indigo-950/45
                            `
                            : `
                              border-transparent
                              bg-transparent
                              hover:border-gray-200
                              hover:bg-gray-50
                              dark:hover:border-gray-800
                              dark:hover:bg-gray-950/50
                            `
                        }
                      `}
                    >
                      <span
                        className={`
                          flex
                          h-9
                          w-9
                          shrink-0
                          items-center
                          justify-center
                          rounded-xl
                          border
                          transition-all
                          ${
                            active
                              ? `
                                border-indigo-200
                                bg-white
                                text-indigo-600
                                dark:border-indigo-800
                                dark:bg-gray-900
                                dark:text-indigo-300
                              `
                              : `
                                border-gray-200
                                bg-gray-50
                                text-gray-500
                                dark:border-gray-800
                                dark:bg-gray-900
                                dark:text-gray-400
                              `
                          }
                        `}
                      >
                        {service.icon}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span
                          className={`
                            block
                            truncate
                            text-[11px]
                            font-bold
                            ${
                              active
                                ? "text-indigo-700 dark:text-indigo-300"
                                : "text-gray-800 dark:text-gray-200"
                            }
                          `}
                        >
                          {service.title}
                        </span>

                        <span
                          className="
                            mt-0.5
                            block
                            text-[8px]
                            font-medium
                            text-gray-400
                          "
                        >
                          Capability {String(index + 1).padStart(2, "0")}
                        </span>
                      </span>

                      <HiArrowRight
                        className={`
                          shrink-0
                          text-sm
                          transition-all
                          ${
                            active
                              ? "translate-x-0.5 text-indigo-500"
                              : "text-gray-300 group-hover:text-indigo-400"
                          }
                        `}
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active service */}
            <motion.div
              key={selectedService?.title}
              initial={{
                opacity: 0,
                y: 10,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.3,
              }}
              className="
                relative
                overflow-hidden
                rounded-[28px]
                border
                border-gray-200
                bg-gray-950
                p-6
                shadow-[0_20px_60px_rgba(15,23,42,0.12)]
                sm:p-8
                lg:min-h-[430px]
                dark:border-gray-800
                dark:bg-black
              "
            >
              {/* Dynamic background */}
              <div
                aria-hidden="true"
                className="
                  pointer-events-none
                  absolute
                  right-[-80px]
                  top-[-80px]
                  h-60
                  w-60
                  rounded-full
                  bg-indigo-500/20
                  blur-[80px]
                "
              />

              <div
                aria-hidden="true"
                className="
                  pointer-events-none
                  absolute
                  bottom-[-100px]
                  left-1/4
                  h-64
                  w-64
                  rounded-full
                  bg-cyan-400/10
                  blur-[90px]
                "
              />

              <div className="relative z-10 flex h-full flex-col">

                {/* Header */}
                <div
                  className="
                    flex
                    items-start
                    justify-between
                    gap-5
                  "
                >
                  <div
                    className="
                      flex
                      h-14
                      w-14
                      items-center
                      justify-center
                      rounded-2xl
                      border
                      border-white/10
                      bg-white/10
                      text-cyan-300
                      backdrop-blur
                      sm:h-16
                      sm:w-16
                    "
                  >
                    {selectedService.icon}
                  </div>

                  <span
                    className="
                      rounded-full
                      border
                      border-white/10
                      bg-white/5
                      px-3
                      py-1.5
                      text-[8px]
                      font-bold
                      uppercase
                      tracking-[0.14em]
                      text-white/55
                    "
                  >
                    Selected capability
                  </span>
                </div>

                {/* Main content */}
                <div className="mt-10 max-w-2xl">
                  <p
                    className="
                      text-[8px]
                      font-bold
                      uppercase
                      tracking-[0.16em]
                      text-cyan-300
                    "
                  >
                    IA4Solutions Service
                  </p>

                  <h3
                    className="
                      mt-2
                      text-3xl
                      font-black
                      leading-[1]
                      tracking-[-0.045em]
                      text-white
                      sm:text-4xl
                      lg:text-[3rem]
                    "
                  >
                    {selectedService.title}
                  </h3>

                  <p
                    className="
                      mt-5
                      max-w-xl
                      text-sm
                      leading-7
                      text-white/65
                    "
                  >
                    {selectedService.description}
                  </p>
                </div>

                {/* Footer */}
                <div
                  className="
                    mt-auto
                    flex
                    flex-wrap
                    items-center
                    justify-between
                    gap-4
                    pt-10
                  "
                >
                  <div
                    className="
                      flex
                      flex-wrap
                      items-center
                      gap-x-5
                      gap-y-2
                    "
                  >
                    <span
                      className="
                        inline-flex
                        items-center
                        gap-1.5
                        text-[9px]
                        font-semibold
                        text-white/55
                      "
                    >
                      <HiCheckCircle className="text-emerald-400" />
                      Practical delivery
                    </span>

                    <span
                      className="
                        inline-flex
                        items-center
                        gap-1.5
                        text-[9px]
                        font-semibold
                        text-white/55
                      "
                    >
                      <HiCheckCircle className="text-emerald-400" />
                      Industry-focused
                    </span>
                  </div>

                  <Link
                    to="/contact"
                    className="
                      group
                      inline-flex
                      items-center
                      gap-2
                      rounded-xl
                      bg-white
                      px-4
                      py-2.5
                      text-[10px]
                      font-bold
                      text-gray-950
                      transition-all
                      duration-200
                      hover:-translate-y-0.5
                      hover:bg-indigo-50
                    "
                  >
                    Talk About This Service

                    <HiArrowUpRight
                      className="
                        transition-transform
                        duration-200
                        group-hover:translate-x-0.5
                      "
                    />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* =====================================================
          FULL SERVICE PORTFOLIO
      ====================================================== */}

      <section
        className="
          relative
          border-t
          border-gray-200/70
          bg-white/65
          px-4
          py-14
          sm:px-6
          sm:py-16
          lg:px-8
          lg:py-20
          dark:border-gray-800
          dark:bg-gray-950/55
        "
      >
        <div className="mx-auto max-w-7xl">

          <div
            className="
              flex
              flex-col
              gap-4
              sm:flex-row
              sm:items-end
              sm:justify-between
            "
          >
            <div>
              <p
                className="
                  text-[8px]
                  font-bold
                  uppercase
                  tracking-[0.16em]
                  text-indigo-600
                  dark:text-indigo-400
                "
              >
                Full service portfolio
              </p>

              <h2
                className="
                  mt-2
                  text-2xl
                  font-black
                  tracking-[-0.04em]
                  text-gray-950
                  sm:text-3xl
                  dark:text-white
                "
              >
                Everything you can access.
              </h2>

              <p
                className="
                  mt-2
                  max-w-2xl
                  text-xs
                  leading-6
                  text-gray-500
                  sm:text-sm
                  dark:text-gray-400
                "
              >
                Explore the complete IA4Solutions service portfolio
                in one focused view.
              </p>
            </div>

            <div
              className="
                flex
                shrink-0
                items-center
                gap-2
                rounded-full
                border
                border-gray-200
                bg-white
                px-3
                py-2
                text-[9px]
                font-bold
                text-gray-500
                shadow-sm
                dark:border-gray-800
                dark:bg-gray-900
                dark:text-gray-400
              "
            >
              <HiSparkles className="text-indigo-500" />
              {portfolioLabel}
            </div>
          </div>

          <motion.div
            className="
              mt-8
              grid
              grid-cols-1
              gap-5
              sm:grid-cols-2
              lg:grid-cols-3
              xl:grid-cols-4
            "
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.08,
            }}
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.06,
                },
              },
            }}
          >
            {services.map((service, index) => (
              <motion.div
                key={
                  service.slug ||
                  `${service.title}-${index}`
                }
                variants={{
                  hidden: {
                    opacity: 0,
                    y: 14,
                  },
                  visible: {
                    opacity: 1,
                    y: 0,
                  },
                }}
              >
                <ServiceCard
                  title={service.title}
                  icon={service.icon}
                  description={service.description}
                  gradient={service.gradient}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* =====================================================
          FINAL CTA
      ====================================================== */}

      <section
        className="
          px-4
          py-12
          sm:px-6
          sm:py-16
          lg:px-8
          lg:py-20
        "
      >
        <motion.div
          initial={{
            opacity: 0,
            y: 14,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.45,
          }}
          className="
            relative
            mx-auto
            max-w-6xl
            overflow-hidden
            rounded-[28px]
            bg-gray-950
            px-6
            py-10
            text-center
            shadow-[0_24px_70px_rgba(15,23,42,0.13)]
            sm:px-10
            sm:py-14
            lg:px-14
            lg:py-16
            dark:bg-black
          "
        >
          <div
            aria-hidden="true"
            className="
              pointer-events-none
              absolute
              left-1/2
              top-[-100px]
              h-72
              w-[500px]
              -translate-x-1/2
              rounded-full
              bg-indigo-500/20
              blur-[100px]
            "
          />

          <div className="relative z-10">
            <p
              className="
                text-[8px]
                font-bold
                uppercase
                tracking-[0.16em]
                text-cyan-300
              "
            >
              Let's build something useful
            </p>

            <h2
              className="
                mx-auto
                mt-3
                max-w-3xl
                text-3xl
                font-black
                leading-tight
                tracking-[-0.045em]
                text-white
                sm:text-4xl
                lg:text-5xl
              "
            >
              Have a goal?
              <span className="text-indigo-300">
                {" "}Let's turn it into the next step.
              </span>
            </h2>

            <p
              className="
                mx-auto
                mt-4
                max-w-2xl
                text-sm
                leading-7
                text-white/60
              "
            >
              Tell us what you are trying to learn, build,
              improve or launch, and we can start from there.
            </p>

            <div className="mt-7">
              <Link
                to="/contact"
                className="
                  group
                  inline-flex
                  items-center
                  gap-2
                  rounded-xl
                  bg-white
                  px-5
                  py-3
                  text-[11px]
                  font-bold
                  text-gray-950
                  shadow-lg
                  transition-all
                  duration-200
                  hover:-translate-y-0.5
                  hover:bg-indigo-50
                "
              >
                Request a Consultation

                <HiArrowRight
                  className="
                    transition-transform
                    duration-200
                    group-hover:translate-x-0.5
                  "
                />
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
    </main>
  );
};

export default Services;